/**
 * Adım 3 — konsey kaynağı seçimi.
 *
 * BULUNAN HATA (kullanıcı bildirimi): adım 3'e girer girmez otomatik LLM
 * persona üretimi tetikleniyordu. Sonuç: kullanıcı kütüphanesinde hazır
 * takımı/birimi olduğu hâlde bunu seçme fırsatı bulamadan bir konsey
 * (ve LLM maliyeti) oluşuyor, sonra da "neden hep otomatik personaya
 * geçiyor?" sorusuyla karşılaşıyordu.
 *
 * Düzeltme: üretim artık ancak `councilSource === "auto"` seçildiğinde
 * çalışır. Kütüphane boşsa bu ekran kendini gösterme zahmetine girmez ve
 * doğrudan otomatiğe düşer — yeni kullanıcı gereksiz bir soruyla
 * karşılaşmasın.
 */
import { useEffect, useMemo, useState } from "react";
import { Sparkles, Users2, Building2, UserPlus, ListChecks } from "lucide-react";
import {
  unitAncestors,
  UNIT_KIND_ICONS,
  UNIT_KIND_LABELS,
  type CouncilSource,
  type PersonaUnit,
} from "../../domain/personaLibrary";
import { usePersonaLibraryStore } from "../../store/personaLibraryStore";
import { useDebateStore } from "../../store/debateStore";
import { cn } from "../common/cn";

function Avatar({
  url,
  name,
  color,
  size = 22,
}: {
  url: string | null;
  name: string;
  color: string;
  size?: number;
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toLocaleUpperCase("tr-TR");
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/15 bg-surface-2/60 text-[9px] font-semibold text-fg-strong"
      style={{ width: size, height: size, boxShadow: `0 0 0 2px ${color}33` }}
    >
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : initials}
    </span>
  );
}

function OptionCard({
  icon,
  title,
  subtitle,
  selected,
  disabled,
  badge,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  selected: boolean;
  // `exactOptionalPropertyTypes` açık — çağıranlar koşullu ifadelerden
  // `undefined` geçiriyor, tipte AÇIKÇA yazılmalı.
  disabled?: boolean | undefined;
  badge?: string | undefined;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex flex-col items-start gap-1.5 rounded-xl border p-3.5 text-left transition-all duration-150",
        "disabled:cursor-not-allowed disabled:opacity-40",
        selected
          ? "border-accent-300/60 bg-[hsl(var(--accent)/0.12)] shadow-[0_0_0_1px_hsl(var(--accent)/0.4)]"
          : "border-line-soft/60 bg-surface-2/30 hover:border-accent-300/40 hover:bg-surface-2/50",
      )}
    >
      <span className="flex w-full items-center gap-2">
        <span className={selected ? "text-accent-200" : "text-fg-subtle"}>{icon}</span>
        <span className="flex-1 text-[13px] font-semibold text-fg-strong">{title}</span>
        {badge && (
          <span className="rounded-full border border-line-soft/60 bg-surface-2/60 px-1.5 py-0.5 text-[10px] text-fg-subtle">
            {badge}
          </span>
        )}
      </span>
      <span className="text-[11.5px] leading-relaxed text-fg-subtle">{subtitle}</span>
    </button>
  );
}

/** Ağaç sırasını koruyan düz liste (girinti `depth` ile). */
function flattenUnits(units: PersonaUnit[]): PersonaUnit[] {
  return [...units].sort((a, b) => a.path.localeCompare(b.path));
}

export function CouncilSourcePicker({ disabled }: { disabled?: boolean }) {
  const { personas, teams, units, loaded, unavailable, load } = usePersonaLibraryStore();
  const source = useDebateStore((s) => s.wizard.councilSource);
  const teamId = useDebateStore((s) => s.wizard.libraryTeamId);
  const unitId = useDebateStore((s) => s.wizard.libraryUnitId);
  const personaIds = useDebateStore((s) => s.wizard.libraryPersonaIds);
  const setSource = useDebateStore((s) => s.setCouncilSource);
  const setTeam = useDebateStore((s) => s.setLibraryTeam);
  const setUnit = useDebateStore((s) => s.setLibraryUnit);
  const togglePersona = useDebateStore((s) => s.toggleLibraryPersona);

  const [ready, setReady] = useState(false);

  useEffect(() => {
    void load().finally(() => setReady(true));
  }, [load]);

  const activePersonas = useMemo(
    () => personas.filter((p) => !p.isArchived),
    [personas],
  );
  const usableTeams = useMemo(() => teams.filter((t) => t.members.length > 0), [teams]);
  const usableUnits = useMemo(
    () => flattenUnits(units.filter((u) => u.totalCount > 0)),
    [units],
  );
  const hasLibrary =
    usableTeams.length > 0 || usableUnits.length > 0 || activePersonas.length > 0;

  // Kütüphane yoksa seçim sunmanın anlamı yok — sessizce otomatiğe geç.
  // `ready` beklenir ki ilk karede yanlışlıkla otomatik kilitlenmesin.
  useEffect(() => {
    if (!ready) return;
    if (source === null && (unavailable || (loaded && !hasLibrary))) {
      setSource("auto");
    }
  }, [ready, source, unavailable, loaded, hasLibrary, setSource]);

  // Kütüphane boş olsa bile panel görünür kalır: "Adaylardan Seç" kalıcı
  // kimlik gerektirmez ve tam da kütüphanesi olmayan operatör için vardır.
  // Panel kendini gizlediği sürece o kullanıcı seçeneği hiç göremiyordu.
  if (!ready) return null;

  return (
    <section className="space-y-3 rounded-xl border border-line-soft/60 bg-surface-2/20 p-3.5">
      <div>
        <h3 className="text-[13px] font-semibold text-fg-strong">
          Konseyi nasıl kuralım?
        </h3>
        <p className="mt-0.5 text-[11.5px] leading-relaxed text-fg-subtle">
          Model konseyi baştan sona kendi tasarlayabilir, sana aday listesi
          sunup seçimi sana bırakabilir ya da kütüphanendeki kalıcı kimlikleri
          masaya çağırabilirsin. Kalıcı kimlikler geçmiş tartışmaları hatırlar
          ve senin geri bildirimlerinden öğrendikleriyle gelir.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <OptionCard
          icon={<Sparkles className="h-4 w-4" />}
          title="Otomatik Üret"
          subtitle="Model sorunun bağlamından yeni uzmanlar tasarlasın."
          selected={source === "auto"}
          {...(disabled ? { disabled: true } : {})}
          onClick={() => setSource("auto")}
        />
        <OptionCard
          icon={<ListChecks className="h-4 w-4" />}
          title="Adaylardan Seç"
          subtitle="Soruya göre ~24 uzman türü önerilsin, kadroyu sen kur."
          selected={source === "catalog"}
          {...(disabled ? { disabled: true } : {})}
          onClick={() => setSource("catalog")}
        />
        <OptionCard
          icon={<Users2 className="h-4 w-4" />}
          title="Hazır Takım"
          subtitle="Kütüphanende kurduğun bir kadroyu olduğu gibi kullan."
          badge={usableTeams.length ? String(usableTeams.length) : undefined}
          selected={source === "team"}
          disabled={disabled || usableTeams.length === 0}
          onClick={() => setSource("team")}
        />
        <OptionCard
          icon={<Building2 className="h-4 w-4" />}
          title="Organizasyon Birimi"
          subtitle="Bir departman/birim tümüyle masaya otursun (alt birimler dahil)."
          badge={usableUnits.length ? String(usableUnits.length) : undefined}
          selected={source === "unit"}
          disabled={disabled || usableUnits.length === 0}
          onClick={() => setSource("unit")}
        />
        <OptionCard
          icon={<UserPlus className="h-4 w-4" />}
          title="Tek Tek Seç"
          subtitle="Kütüphaneden istediğin personaları kendin bir araya getir."
          badge={activePersonas.length ? String(activePersonas.length) : undefined}
          selected={source === "picked"}
          disabled={disabled || activePersonas.length === 0}
          onClick={() => setSource("picked")}
        />
      </div>

      {source === "team" && (
        <div className="space-y-1.5 border-t border-line-soft/40 pt-3">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-fg-subtle">
            Takım seç
          </div>
          <div className="flex flex-wrap gap-1.5">
            {usableTeams.map((team) => (
              <button
                key={team.id}
                type="button"
                disabled={disabled}
                // Topoloji ARTIK takımdan gelmiyor: bir görev gücü farklı
                // soru tipleri için yeniden kullanılır, doğru tartışma düzeni
                // soruya göre değişir (2. adımda seçilir).
                onClick={() => setTeam(team.id)}
                className={cn(
                  "rounded-lg border px-2.5 py-1.5 text-left transition-colors",
                  team.id === teamId
                    ? "border-accent-300/60 bg-[hsl(var(--accent)/0.16)]"
                    : "border-line-soft/60 bg-surface-2/40 hover:border-accent-300/40",
                )}
              >
                <span className="block text-[12px] font-medium text-fg-strong">
                  {team.name}
                </span>
                <span className="block text-[10.5px] text-fg-subtle">
                  {team.members.length} üye
                  {(() => {
                    const distinct = new Set(
                      team.members.map((m) => m.unitId ?? "—"),
                    ).size;
                    return distinct > 1 ? ` · ${distinct} birim` : "";
                  })()}
                </span>
              </button>
            ))}
          </div>
          {teamId && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {usableTeams
                .find((t) => t.id === teamId)
                ?.members.map((m) => (
                  <span
                    key={m.id}
                    className="flex items-center gap-1.5 rounded-full border border-white/10 bg-surface-2/50 py-0.5 pl-0.5 pr-2"
                  >
                    <Avatar url={m.avatarUrl} name={m.displayName} color={m.themeColor} />
                    <span className="text-[11px] text-fg-muted">{m.displayName}</span>
                  </span>
                ))}
            </div>
          )}
        </div>
      )}

      {source === "unit" && (
        <div className="space-y-1.5 border-t border-line-soft/40 pt-3">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-fg-subtle">
            Birim seç — alt birimlerdeki personalar da gelir
          </div>
          <div className="flex max-h-52 flex-col gap-1 overflow-y-auto pr-1">
            {usableUnits.map((unit) => {
              // Üst zincir ("Cumhurbaşkanlığı › SSB") ayrı bir satırda:
              // yalnız yaprak adı gösterildiğinde iki farklı kurumun
              // benzer adlı müdürlükleri ayırt edilemiyordu.
              const parents = unitAncestors(units, unit.id)
                .slice(0, -1)
                .map((u) => u.name)
                .join(" › ");
              return (
                <button
                  key={unit.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => setUnit(unit.id)}
                  style={{ paddingLeft: 10 + unit.depth * 10 }}
                  title={parents ? `${parents} › ${unit.name}` : unit.name}
                  className={cn(
                    "flex items-start gap-2 rounded-lg border py-1.5 pr-2.5 text-left transition-colors",
                    unit.id === unitId
                      ? "border-accent-300/60 bg-[hsl(var(--accent)/0.16)]"
                      : "border-line-soft/60 bg-surface-2/40 hover:border-accent-300/40",
                  )}
                >
                  <span className="mt-px text-[11px]" aria-hidden>
                    {UNIT_KIND_ICONS[unit.kind]}
                  </span>
                  <span className="min-w-0 flex-1">
                    {parents && (
                      <span className="block truncate text-[10px] text-fg-subtle">
                        {parents}
                      </span>
                    )}
                    <span className="block break-words text-[12px] leading-snug text-fg-strong">
                      {unit.name}
                    </span>
                  </span>
                  <span className="mt-px shrink-0 text-[10px] text-fg-subtle">
                    {UNIT_KIND_LABELS[unit.kind]}
                  </span>
                  <span className="mt-px shrink-0 text-[10.5px] tabular-nums text-accent-300/80">
                    {unit.totalCount} kişi
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {source === "picked" && (
        <div className="space-y-1.5 border-t border-line-soft/40 pt-3">
          <div className="flex items-baseline justify-between">
            <span className="text-[10.5px] font-semibold uppercase tracking-wider text-fg-subtle">
              Persona seç
            </span>
            <span className="text-[10.5px] text-fg-subtle">
              {personaIds.length} seçili
            </span>
          </div>
          <div className="flex max-h-52 flex-wrap gap-1.5 overflow-y-auto pr-1">
            {activePersonas.map((p) => {
              const selected = personaIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => togglePersona(p.id)}
                  title={p.expertise ?? p.title}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2.5 transition-colors",
                    selected
                      ? "border-accent-300/60 bg-[hsl(var(--accent)/0.16)]"
                      : "border-line-soft/60 bg-surface-2/40 hover:border-accent-300/40",
                  )}
                >
                  <Avatar url={p.avatarUrl} name={p.displayName} color={p.themeColor} />
                  <span className="text-[11.5px] text-fg-muted">{p.displayName}</span>
                  {p.stats.memoryCount > 0 && (
                    <span
                      className="text-[10px] text-accent-300/80"
                      title={`${p.stats.memoryCount} hafıza notu`}
                    >
                      {p.stats.memoryCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export type { CouncilSource };
